<script setup lang="ts">
import AppLayout from '../layouts/AppLayout.vue'
import HealthStatus from '../components/HealthStatus.vue'
import SupplierLogo from '../components/SupplierLogo.vue'
import Skeleton from '../components/Skeleton.vue'
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuth } from '../composables/useAuth'
import { getSupplierDashboard, getSupplierEngagement } from '../services/api'
import type { SupplierDashboard } from '../types/supplier'
import { useCurrentContext } from '../composables/useCurrentContext'
import { trackEvent } from '../services/analytics'
import * as Sentry from '@sentry/vue'

const router = useRouter()
const { supplierId } = useCurrentContext()
const { getAccessTokenSilently } = useAuth()

const loading = ref(true)
const error = ref<string | null>(null)
const dashboard = ref<SupplierDashboard | null>(null)

// The metrics catalog is ENTERPRISE-only; show the customize link only when reachable.
const canCustomizeMetrics = computed(() => {
  const tier = (dashboard.value as any)?.supplier?.tier
  return tier === 'ENTERPRISE'
})

async function loadDashboard() {
  try {
    loading.value = true
    if (!supplierId.value) {
      throw new Error('No supplier context available')
    }
    const token = await getAccessTokenSilently()
    const data = await getSupplierDashboard(supplierId.value, token)
    dashboard.value = data
    trackEvent('supplier_dashboard_viewed', { supplier_id: supplierId.value })
  } catch (e: any) {
    console.error(e)
    error.value = "Unable to load supplier dashboard data right now."
    // Report so we get alerted instead of the supplier hitting a silent wall. Skip
    // the EXPECTED upgrade gate (403 with requiredTier on a lower-tier supplier) —
    // that's product-by-design, not a fault. Everything else (missing supplier
    // context, 500s, network) is worth an alert.
    const status = e?.response?.status
    const isExpectedUpgradeGate = status === 403 && !!e?.response?.data?.requiredTier
    if (!isExpectedUpgradeGate) {
      Sentry.captureException(e, {
        tags: { feature: 'supplier-dashboard' },
        extra: { supplierId: supplierId.value, status, apiError: e?.response?.data?.error },
      })
    }
  } finally {
    loading.value = false
  }
}

// ── Advisor visibility / engagement ──
interface Engagement { total: number; byType: Record<string, number>; uniqueAdvisors: number; days: number }
const engagement = ref<Engagement | null>(null)
const engagementLoading = ref(true)
const engagementDays = ref(30)
const ENGAGEMENT_METRICS: { key: string; label: string }[] = [
  { key: 'PAGE_VIEW', label: 'Profile views' },
  { key: 'SEARCH_SURFACED', label: 'Search appearances' },
  { key: 'SHORTLISTED', label: 'Shortlisted' },
  { key: 'COMPARED', label: 'Compared' },
  { key: 'PRESENTED_FOR_SOLUTION', label: 'Deep analysis' },
]

async function loadEngagement() {
  if (!supplierId.value) return
  engagementLoading.value = true
  try {
    const token = await getAccessTokenSilently()
    engagement.value = await getSupplierEngagement(supplierId.value, engagementDays.value, token)
  } catch { engagement.value = null }
  finally { engagementLoading.value = false }
}

function setEngagementWindow(days: number) {
  engagementDays.value = days
  loadEngagement()
}

onMounted(() => {
  loadDashboard()
  loadEngagement()
})

const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val)
}

const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString()
}

const goToNewPromotion = () => {
    router.push('/supplier/promotions?create=true')
}
</script>

<template>
  <AppLayout>
    <div class="space-y-5">
      <!-- Header -->
      <div v-if="dashboard" class="flex flex-col md:flex-row md:items-center justify-between gap-4">
         <div class="flex items-center gap-4">
            <SupplierLogo :src="dashboard.supplier?.logoUrl" :website="dashboard.supplier?.websiteUrl" :alt="dashboard.supplier?.name || 'Logo'" class="w-16 h-16 object-contain shadow-sm border border-cc-border rounded-lg bg-white p-2" />
            <div>
                <h1 class="text-3xl font-bold text-cc-text">{{ dashboard.supplier?.name }}</h1>
                <div class="flex items-center gap-2 mt-1">
                    <span class="text-xs text-cc-text-tertiary">Feed Synced: {{ dashboard.metrics?.suppliersTxtStatus?.lastSyncAt ? new Date(dashboard.metrics.suppliersTxtStatus.lastSyncAt).toLocaleTimeString() : 'N/A' }}</span>
                </div>
            </div>
         </div>
         <div>
           <button @click="goToNewPromotion" class="inline-flex items-center gap-1.5 bg-cc-primary dark:bg-cc-primary-strong text-cc-btn-text px-4 py-2 rounded-lg hover:bg-cc-primary-hover dark:hover:bg-cc-primary transition text-sm font-semibold">
             <span class="text-base leading-none">+</span>
             <span>New Promotion</span>
           </button>
         </div>
      </div>
      <div v-else-if="loading" aria-busy="true" class="flex items-center gap-4">
        <Skeleton class="w-16 h-16 rounded-lg" />
        <div class="space-y-2">
          <Skeleton class="h-8 w-64" />
          <Skeleton class="h-3 w-40" />
        </div>
      </div>

      <!-- Health Check (keep visible for demo/debug) -->
      <HealthStatus />

      <!-- Error State -->
      <div v-if="error" class="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md">
          {{ error }}
      </div>

      <!-- Metrics -->
      <div v-if="dashboard || loading" class="bg-cc-elevated rounded-lg shadow-sm border border-cc-border p-6">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-lg font-semibold text-cc-text">Metrics</h2>
          <!-- Link to Metrics Catalog — the catalog is ENTERPRISE-only, so don't send
               lower-tier suppliers to a page that 403s. -->
          <router-link v-if="dashboard && canCustomizeMetrics" to="/supplier/metrics" class="text-sm font-medium text-cc-primary hover:underline">
            Customize →
          </router-link>
        </div>
        <div v-if="dashboard" class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <MetricTile
              v-for="tile in dashboard.tiles"
              :key="tile.id"
              :label="tile.label"
              :value="tile.value"
              :color="tile.color"
              :title="tile.description"
          />

          <!-- Empty State Tile -->
          <router-link
              v-if="dashboard.tiles && dashboard.tiles.length < 8"
              to="/supplier/metrics"
              class="bg-cc-deep p-4 rounded-lg border-2 border-dashed border-cc-border flex flex-col justify-center items-center h-full hover:border-gray-300 dark:hover:border-gray-600 transition group"
          >
              <span class="text-2xl mb-1 text-gray-400 group-hover:text-gray-600 dark:text-gray-500 dark:group-hover:text-gray-300">+</span>
              <span class="text-xs font-medium text-gray-500 group-hover:text-gray-700 dark:text-gray-400 dark:group-hover:text-gray-200">Add Metric</span>
          </router-link>
        </div>
        <div v-else aria-busy="true" class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <Skeleton v-for="i in 8" :key="i" class="h-[88px] rounded-lg" />
        </div>
      </div>

      <!-- Advisor Visibility / Engagement -->
      <div v-if="dashboard || loading" class="bg-cc-elevated rounded-lg shadow-sm border border-cc-border p-6">
        <div class="flex items-center justify-between mb-4">
          <div>
            <h2 class="text-lg font-semibold text-cc-text">Advisor Visibility</h2>
            <p class="text-sm text-cc-text-tertiary">How advisors are engaging with your company<template v-if="(engagement?.uniqueAdvisors ?? 0) > 0"> · reached {{ engagement?.uniqueAdvisors }} advisor(s)</template></p>
          </div>
          <div class="flex rounded-lg border border-cc-border overflow-hidden text-sm">
            <button v-for="d in [30, 90]" :key="d" @click="setEngagementWindow(d)"
              class="px-3 py-1.5 transition-colors"
              :class="engagementDays === d ? 'bg-cc-primary dark:bg-cc-primary-strong text-cc-btn-text font-semibold' : 'text-cc-text-secondary hover:bg-cc-hover'">
              {{ d }}d
            </button>
          </div>
        </div>
        <div v-if="engagementLoading" aria-busy="true" class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <Skeleton v-for="i in 5" :key="i" class="h-[84px] rounded-lg" />
        </div>
        <div v-else-if="(engagement?.total ?? 0) === 0" class="text-sm text-cc-text-tertiary italic py-3">
          No advisor engagement recorded in this window yet. As advisors view, shortlist, and compare your company, it will show here.
        </div>
        <div v-else class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <div v-for="m in ENGAGEMENT_METRICS" :key="m.key" class="text-center p-4 bg-cc-deep rounded-lg border border-cc-border">
            <div class="text-2xl font-bold text-cc-primary">{{ (engagement?.byType?.[m.key] ?? 0).toLocaleString() }}</div>
            <div class="text-xs text-cc-text-tertiary mt-1">{{ m.label }}</div>
          </div>
        </div>
      </div>

      <!-- Top Deals Table -->
      <div class="bg-cc-elevated rounded-lg shadow-sm border border-cc-border overflow-hidden">
        <div class="px-6 py-4 border-b border-cc-border bg-gray-50 dark:bg-gray-700/50 flex justify-between items-center">
             <h3 class="font-bold text-gray-700 dark:text-white">Top Deals (Closed Won)</h3>
             <span class="text-xs text-gray-400">Last 90 Days</span>
        </div>
        
        <div v-if="loading" aria-busy="true" class="divide-y divide-gray-50 dark:divide-gray-700/50">
            <div v-for="i in 4" :key="i" class="grid grid-cols-6 gap-6 px-6 py-4">
                <Skeleton class="h-4 w-3/4" />
                <Skeleton class="h-4 w-2/3" />
                <Skeleton class="h-4 w-1/2 justify-self-end" />
                <Skeleton class="h-4 w-1/2 justify-self-end" />
                <Skeleton class="h-4 w-2/3" />
                <Skeleton class="h-4 w-full" />
            </div>
        </div>
        
        <div v-else-if="dashboard && dashboard.topDeals?.length > 0" class="overflow-x-auto">
            <table class="w-full text-sm text-left">
                <thead class="text-xs text-gray-500 uppercase bg-gray-50 dark:bg-gray-700/50 border-b border-cc-border">
                    <tr>
                        <th class="px-6 py-3 dark:text-gray-400">Deal Name</th>
                        <th class="px-6 py-3 dark:text-gray-400">Advisor</th>
                        <th class="px-6 py-3 text-right dark:text-gray-400">MRR</th>
                        <th class="px-6 py-3 text-right dark:text-gray-400">NRR</th>
                        <th class="px-6 py-3 dark:text-gray-400">Date</th>
                        <th class="px-6 py-3 dark:text-gray-400">Products</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="deal in dashboard.topDeals" :key="deal.id" class="border-b border-gray-50 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition">
                        <td class="px-6 py-4 font-medium text-cc-text">{{ deal.dealName }}</td>
                        <td class="px-6 py-4 text-cc-text-secondary">{{ deal.advisorName }}</td>
                        <td class="px-6 py-4 text-right font-medium text-green-700 dark:text-green-400">{{ formatCurrency(deal.amountMrr) }}</td>
                        <td class="px-6 py-4 text-right text-cc-text-secondary">{{ formatCurrency(deal.amountNrr) }}</td>
                        <td class="px-6 py-4 text-cc-text-secondary">{{ formatDate(deal.dealDate) }}</td>
                        <td class="px-6 py-4 text-cc-text-secondary truncate max-w-xs" :title="deal.summaryProducts.join(', ')">
                            {{ (deal.summaryProducts || []).slice(0, 2).join(', ') }}
                            <span v-if="(deal.summaryProducts || []).length > 2" class="text-xs bg-cc-hover rounded px-1 text-cc-text-secondary">+{{ (deal.summaryProducts || []).length - 2 }}</span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        
        <div v-else-if="dashboard" class="p-12 text-center text-gray-500">
            No closed deals found for this period.
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script lang="ts">
// Simple internal component for tiles
import { defineComponent, h } from 'vue'
const MetricTile = defineComponent({
    props: ['label', 'value', 'color'],
    render() {
        return h('div', { class: 'bg-cc-deep p-4 rounded-lg border border-cc-border flex flex-col justify-between h-full' }, [
            h('div', { class: 'text-cc-text-tertiary text-xs uppercase tracking-wider font-semibold mb-1' }, this.label),
            h('div', { class: `text-2xl font-bold ${this.color || 'text-cc-text'}` }, this.value)
        ])
    }
})
</script>
